import React, { useCallback, useEffect, useState } from 'react';
import { SenseHint } from './SenseChooser.js';
import { Button, Card as AntCard, Flex, Tooltip, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Card, TranslationLanguage, translationFor, upNextCards } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { getCloudDecksApi } from '../services/cloudDecks.js';
import { forgetPendingCards } from '../services/pendingCardStore.js';
import { loadFollowedDeck, nextDeckQuestion } from '../services/reviewFlow.js';
import { plural } from '../options/format.js';
import { CHROMATIC_PALETTES } from '../theme/index.js';
import { TranslationText } from './TranslationText.js';
import { tourTarget } from '../options/tourTargets.js';

const { Text } = Typography;

/** Rows shown before "and N more". */
export const UP_NEXT_ROWS = 10;

interface FollowedInfo {
  title: string;
  /** The word Echo asks about next (1-based) and the deck's size; null when the deck is finished. */
  next: { position: number; total: number } | null;
}

interface Props {
  /** Bump to reload (the dashboard reloads when cards changed elsewhere). */
  refreshKey?: number;
  /** The language the meanings are shown in. */
  language: TranslationLanguage;
  /** Called after a word was removed from the list. */
  onChanged?: () => void;
}

const tagStyle: React.CSSProperties = {
  padding: '2px 10px',
  borderRadius: 999,
  background: '#e6f4ff',
  color: '#0958d9',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

/**
 * "Up next to learn": the words waiting to be introduced, in the order they will be (words the
 * learner added first, then words chosen in decks), what they mean, where each came from, and a
 * way to take one off the list. Below it, what happens when the list runs out.
 */
export const UpNextCard: React.FC<Props> = ({ refreshKey = 0, language, onChanged }) => {
  const [waiting, setWaiting] = useState<Card[] | null>(null);
  const [deckTitles, setDeckTitles] = useState<Record<string, string>>({});
  const [followed, setFollowed] = useState<FollowedInfo | null>(null);

  const load = useCallback(async () => {
    const cards = await StorageManager.getCards();
    setWaiting(upNextCards(cards));
    try {
      const api = await getCloudDecksApi();
      const lists = await api.readLists();
      setDeckTitles(Object.fromEntries(lists.decks.map((d) => [d.id, d.title])));
      const deck = await loadFollowedDeck({ api, fetchMissing: false });
      if (!deck) {
        setFollowed(null);
        return;
      }
      const next = await nextDeckQuestion(deck, new Set(), api);
      setFollowed({
        title: deck.title,
        next: next ? { position: next.position, total: next.total } : null,
      });
    } catch (err) {
      console.warn('[LingoPing] Could not read the followed deck:', err);
      setFollowed(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const remove = async (card: Card) => {
    setWaiting((prev) => prev?.filter((c) => c.id !== card.id) ?? prev);
    await StorageManager.deleteCard(card.id);
    await forgetPendingCards([card.id]);
    onChanged?.();
    void load();
  };

  if (waiting === null) return null;

  const shown = waiting.slice(0, UP_NEXT_ROWS);
  const hidden = waiting.length - shown.length;

  return (
    <AntCard
      {...tourTarget('up-next')}
      size="small"
      styles={{ body: { padding: 0 } }}
      title={
        <Flex align="baseline" gap={10}>
          <span>Up next to learn</span>
          <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
            {waiting.length === 0 ? 'nothing waiting' : `${plural(waiting.length, 'word')} waiting`}
          </Text>
        </Flex>
      }
    >
      {shown.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Up next to learn">
          {shown.map((card, index) => {
            const palette = CHROMATIC_PALETTES[card.linguistic_meta?.gender || 'neutral'];
            const article = card.linguistic_meta?.article;
            const preparing = card.content_status === 'pending';
            const meaning = preparing ? null : translationFor(card, language);
            const source =
              card.queue_priority === 1
                ? null
                : ((card.source_deck_id && deckTitles[card.source_deck_id]) ?? 'From a deck');
            return (
              <li
                key={card.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 20px',
                  borderTop: index === 0 ? undefined : '1px solid #f0f0f0',
                  fontSize: 14,
                }}
              >
                <span style={{ width: 20, color: '#5b6472' }}>{index + 1}</span>
                <span style={{ flexGrow: 1, minWidth: 0 }}>
                  {article && (
                    <>
                      <span style={{ color: palette.color, fontWeight: 600 }}>{article}</span>{' '}
                    </>
                  )}
                  <strong>{card.front_text}</strong>
                  <SenseHint hint={card.sense_hint} />{' '}
                  {meaning ? (
                    <TranslationText
                      as="span"
                      text={`· ${meaning.text}`}
                      lang={meaning.textLang}
                      style={{ color: '#475569' }}
                    />
                  ) : (
                    <span style={{ color: '#5b6472', fontStyle: 'italic' }}>· Preparing…</span>
                  )}
                </span>
                {source === null ? (
                  <span style={tagStyle}>Added by you</span>
                ) : (
                  <span style={{ fontSize: 12, color: '#5b6472', textAlign: 'right' }}>
                    {source}
                  </span>
                )}
                <Tooltip title="Remove from the list">
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => void remove(card)}
                    aria-label={`Remove ${card.front_text} from the list`}
                    style={{ color: '#5b6472' }}
                  />
                </Tooltip>
              </li>
            );
          })}
        </ol>
      )}
      {hidden > 0 && (
        <div
          style={{
            padding: '8px 20px',
            fontSize: 13,
            color: '#5b6472',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          and {hidden} more
        </div>
      )}
      <Flex
        vertical
        gap={6}
        style={{
          padding: '14px 20px',
          borderTop: shown.length > 0 ? '1px solid #f0f0f0' : undefined,
          background: '#fafafa',
          borderRadius: '0 0 8px 8px',
          fontSize: 13,
          lineHeight: 1.5,
          color: '#475569',
        }}
      >
        <span>Words you add yourself go first.</span>
        {followed && followed.next && (
          <span>
            When this list is empty, Echo asks about the next word in{' '}
            <strong>{followed.title}</strong> (word {followed.next.position} of{' '}
            {followed.next.total}).
          </span>
        )}
        {followed && !followed.next && (
          <span>
            You&apos;ve been through every word in <strong>{followed.title}</strong>. Follow another
            deck or add words to keep learning.
          </span>
        )}
        {!followed && <span>Follow a deck to get new words here.</span>}
      </Flex>
    </AntCard>
  );
};
